import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComment, faCommentDots } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { PARAGRAPH_NOTE_LIMIT } from "../../hooks/useLessonParagraphNotes";
import { LessonParagraphNote } from "../../utils/lessonParagraphNotesStorage";
import styles from "./paragraphNote.module.scss";

// Above this width there is real room in the margin beside the lessons
// column (see .main's 780px max-width in lessonsPage.module.scss) for a
// slim collapsed marker; below it a margin column would just squeeze the
// content, so the affordance moves inline instead. Kept as a plain number
// (not a shared SCSS variable) so the JS layout switch and the CSS media
// query below can't drift from each other without both being edited.
export const MARGIN_NOTES_BREAKPOINT_PX = 1100;
const MARGIN_NOTES_QUERY = `(min-width: ${MARGIN_NOTES_BREAKPOINT_PX}px)`;

// A stable, HTML-id-safe suffix for the visually-hidden "Has a personal
// note" text a note's paragraph points to via aria-describedby. anchorId
// (e.g. "Lesson 3:p2") contains characters that aren't valid in an id.
export function noteDescriptionId(anchorId: string): string {
    return `paragraph-note-desc-${anchorId.replace(/[^a-zA-Z0-9-]/g, "-")}`;
}

// jsdom in this repo's test setup doesn't implement matchMedia at all, so
// treat that as "assume desktop" (the common case) and let tests that care
// about the mobile layout mock window.matchMedia explicitly.
function useIsMarginLayout(): boolean {
    const supportsMatchMedia = typeof window !== "undefined" && typeof window.matchMedia === "function";
    const [isMarginLayout, setIsMarginLayout] = useState(
        () => !supportsMatchMedia || window.matchMedia(MARGIN_NOTES_QUERY).matches
    );

    useEffect(() => {
        if (!supportsMatchMedia) return undefined;
        const mediaQueryList = window.matchMedia(MARGIN_NOTES_QUERY);
        const onChange = (event: MediaQueryListEvent) => setIsMarginLayout(event.matches);
        mediaQueryList.addEventListener("change", onChange);
        return () => mediaQueryList.removeEventListener("change", onChange);
    }, [supportsMatchMedia]);

    return isMarginLayout;
}

interface ParagraphNoteProps {
    anchorId: string;
    paragraphNumber: number;
    note?: LessonParagraphNote;
    onSave: (anchorId: string, text: string) => void;
}

// A personal, per-user margin note anchored to one lesson paragraph -
// Google-Docs-style comment bubble on desktop, a bottom sheet on narrow
// viewports. Deliberately separate from LessonNote (whole-lesson takeaway).
export default function ParagraphNote({ anchorId, paragraphNumber, note, onSave }: ParagraphNoteProps) {
    const [open, setOpen] = useState(false);
    const isMarginLayout = useIsMarginLayout();
    const hasNote = Boolean(note?.text);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const { t } = useTranslation("common");
    const editorLabel = t("paragraphNote.editorLabel", { number: paragraphNumber });

    // isMarginLayout is a dependency, not just open: crossing the breakpoint
    // while the editor is open unmounts one textarea (margin card or bottom
    // sheet) and mounts the other, so focus must be re-applied to whichever
    // one now exists rather than silently dropping to the body.
    useEffect(() => {
        if (open) textareaRef.current?.focus();
    }, [open, isMarginLayout]);

    function close() {
        setOpen(false);
        triggerRef.current?.focus();
    }

    function onTriggerKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen(true);
        }
    }

    // Escape should close the editor no matter which element inside it
    // currently has focus (the textarea, or the sheet's "Done" button).
    function onEditorKeyDown(event: React.KeyboardEvent<HTMLElement>) {
        if (event.key === "Escape") {
            event.preventDefault();
            close();
        }
    }

    const textarea = (
        <textarea
            ref={textareaRef}
            className={styles.textarea}
            value={note?.text ?? ""}
            maxLength={PARAGRAPH_NOTE_LIMIT}
            rows={4}
            placeholder={t("paragraphNote.placeholder")}
            aria-label={editorLabel}
            aria-describedby={`${anchorId}-hint`}
            onChange={(event) => onSave(anchorId, event.target.value)}
        />
    );
    const hint = (
        <p id={`${anchorId}-hint`} className={styles.hint}>
            {t("paragraphNote.limitHint", { count: (note?.text ?? "").length, limit: PARAGRAPH_NOTE_LIMIT })}
        </p>
    );

    return (
        <div className={styles.wrap}>
            <button
                ref={triggerRef}
                type="button"
                className={`${styles.trigger} ${isMarginLayout ? styles.triggerMargin : styles.triggerInline} ${hasNote ? styles.triggerHasNote : ""}`}
                // The default hover/focus-only reveal for an empty paragraph is
                // driven from lessonsPage.module.scss's .paragraphWrap rule (a
                // different CSS module, hence a plain data attribute instead of
                // a class - CSS Modules hashes class names per-file, so a class
                // selector here couldn't match a class defined in that file).
                data-note-trigger={hasNote ? undefined : "hover"}
                aria-label={hasNote ? t("paragraphNote.viewOrEdit", { number: paragraphNumber }) : t("paragraphNote.add", { number: paragraphNumber })}
                aria-haspopup="dialog"
                aria-expanded={open}
                onClick={() => setOpen((current) => !current)}
                onKeyDown={onTriggerKeyDown}
            >
                <FontAwesomeIcon icon={hasNote ? faCommentDots : faComment} />
            </button>
            {hasNote && (
                <span id={noteDescriptionId(anchorId)} className={styles.visuallyHidden}>
                    {t("paragraphNote.hasNote")}
                </span>
            )}
            {open && isMarginLayout && (
                <div className={styles.marginCard} role="group" aria-label={editorLabel} onKeyDown={onEditorKeyDown}>
                    {textarea}
                    {hint}
                </div>
            )}
            {open && !isMarginLayout && (
                <div className={styles.sheetBackdrop} role="presentation" onClick={close}>
                    <div
                        className={styles.sheet}
                        role="dialog"
                        aria-modal="true"
                        aria-label={editorLabel}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={onEditorKeyDown}
                    >
                        {textarea}
                        {hint}
                        <button type="button" className={styles.sheetClose} onClick={close}>
                            {t("paragraphNote.done")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
